import React, { useState, useEffect } from 'react';
import MainLayout from '../../components/layout/MainLayout';
import { MdCalculate, MdSearch, MdVisibility, MdCheck, MdEdit } from 'react-icons/md';
import '../master/MasterData.css';

interface PayrollDetail {
  employeeCode: string;
  employeeName: string;
  position: string;
  basicSalary: number;
  overtimePay: number;
  allowances: number;
  bonuses: number;
  manualAdjustment: number;
  grossPay: number;
  sssContribution: number;
  philHealthContribution: number;
  pagibigContribution: number;
  withholdingTax: number;
  otherDeductions: number;
  totalDeductions: number;
  netPay: number;
}

interface PayrollModalProps {
  detail: PayrollDetail;
  viewMode: boolean;
  calculationStatus: 'DRAFT' | 'CALCULATED' | 'CONFIRMED';
  onClose: () => void;
  onSave: (detail: PayrollDetail) => void;
}

const PayrollModal: React.FC<PayrollModalProps> = ({ detail, viewMode, calculationStatus, onClose, onSave }) => {
  const [formData, setFormData] = useState<PayrollDetail>({ ...detail });
  const [editMode, setEditMode] = useState(!viewMode);

  const isLocked = calculationStatus === 'CONFIRMED';

  // 자동 계산 함수
  const calculatePayroll = (data: PayrollDetail): PayrollDetail => {
    // 1. Gross Pay 계산
    const grossPay = data.basicSalary + data.overtimePay + data.allowances + data.bonuses + data.manualAdjustment;

    // 2. SSS Contribution 계산 (간단한 예시 - 실제로는 SSS 테이블 참조)
    let sssContribution = 0;
    if (grossPay <= 20000) sssContribution = 900;
    else if (grossPay <= 25000) sssContribution = 1125;
    else if (grossPay <= 30000) sssContribution = 1350;
    else if (grossPay <= 35000) sssContribution = 1575;
    else if (grossPay <= 40000) sssContribution = 1800;
    else sssContribution = 2025;

    // 3. PhilHealth Contribution 계산 (Gross Pay의 2%, 최소/최대 적용)
    let philHealthContribution = grossPay * 0.02;
    if (philHealthContribution < 500) philHealthContribution = 500;
    if (philHealthContribution > 1500) philHealthContribution = 1500;
    philHealthContribution = Math.round(philHealthContribution);

    // 4. Pag-IBIG Contribution 계산 (2%, 최대 200)
    const pagibigContribution = Math.min(Math.round(grossPay * 0.02), 200);

    // 5. Taxable Income 계산
    const taxableIncome = grossPay - (sssContribution + philHealthContribution + pagibigContribution);

    // 6. Withholding Tax 계산 (간단한 예시 - 실제로는 BIR 테이블 참조)
    let withholdingTax = 0;
    const monthlyTaxable = taxableIncome;
    if (monthlyTaxable <= 20833) {
      withholdingTax = 0;
    } else if (monthlyTaxable <= 33332) {
      withholdingTax = (monthlyTaxable - 20833) * 0.15;
    } else if (monthlyTaxable <= 66666) {
      withholdingTax = 1875 + (monthlyTaxable - 33332) * 0.20;
    } else if (monthlyTaxable <= 166666) {
      withholdingTax = 8541.80 + (monthlyTaxable - 66666) * 0.25;
    } else if (monthlyTaxable <= 666666) {
      withholdingTax = 33541.80 + (monthlyTaxable - 166666) * 0.30;
    } else {
      withholdingTax = 183541.80 + (monthlyTaxable - 666666) * 0.35;
    }
    withholdingTax = Math.round(withholdingTax);

    // 7. Total Deductions 계산
    const totalDeductions = sssContribution + philHealthContribution + pagibigContribution + withholdingTax + data.otherDeductions;

    // 8. Net Pay 계산
    const netPay = grossPay - totalDeductions;

    return {
      ...data,
      grossPay,
      sssContribution,
      philHealthContribution,
      pagibigContribution,
      withholdingTax,
      totalDeductions,
      netPay
    };
  };

  const handleFieldChange = (field: keyof PayrollDetail, value: number) => {
    const updatedData = { ...formData, [field]: value };
    const recalculated = calculatePayroll(updatedData);
    setFormData(recalculated);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave(formData);
  };

  const canEdit = !isLocked && editMode;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content modal-large" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>
            {editMode ? 'Edit Payroll' : 'View Payroll'} - {detail.employeeName}
          </h3>
          <button className="close-btn" onClick={onClose}>&times;</button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            {isLocked && (
              <div className="alert alert-warning" style={{
                padding: '10px',
                marginBottom: '15px',
                backgroundColor: '#fff3cd',
                border: '1px solid #ffc107',
                borderRadius: '4px',
                color: '#856404'
              }}>
                ⚠️ This payroll is CONFIRMED and locked. No modifications allowed.
              </div>
            )}

            <h4 className="section-heading">Earnings</h4>
            <table className="item-table">
              <tbody>
                <tr>
                  <td style={{ width: '60%' }}>
                    Basic Salary
                    <div style={{ fontSize: '11px', color: '#666' }}>
                      (Auto-calculated from Employee Master Data)
                    </div>
                  </td>
                  <td className="amount" style={{ backgroundColor: '#f5f5f5' }}>
                    ₱{formData.basicSalary.toLocaleString()}
                  </td>
                </tr>
                <tr>
                  <td>
                    Overtime Pay
                    <div style={{ fontSize: '11px', color: '#666' }}>
                      (Auto-calculated from Timekeeping)
                    </div>
                  </td>
                  <td className="amount" style={{ backgroundColor: '#f5f5f5' }}>
                    ₱{formData.overtimePay.toLocaleString()}
                  </td>
                </tr>
                <tr>
                  <td>
                    Allowances
                    {canEdit && <span style={{ color: '#28a745', fontSize: '11px' }}> (Editable)</span>}
                  </td>
                  <td className="amount">
                    {canEdit ? (
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        value={formData.allowances}
                        onChange={(e) => handleFieldChange('allowances', parseFloat(e.target.value) || 0)}
                        style={{ width: '100%', textAlign: 'right' }}
                      />
                    ) : (
                      `₱${formData.allowances.toLocaleString()}`
                    )}
                  </td>
                </tr>
                <tr>
                  <td>
                    Bonuses
                    {canEdit && <span style={{ color: '#28a745', fontSize: '11px' }}> (Editable)</span>}
                  </td>
                  <td className="amount">
                    {canEdit ? (
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        value={formData.bonuses}
                        onChange={(e) => handleFieldChange('bonuses', parseFloat(e.target.value) || 0)}
                        style={{ width: '100%', textAlign: 'right' }}
                      />
                    ) : (
                      `₱${formData.bonuses.toLocaleString()}`
                    )}
                  </td>
                </tr>
                <tr>
                  <td>
                    Manual Adjustment
                    {canEdit && <span style={{ color: '#28a745', fontSize: '11px' }}> (Editable)</span>}
                    <div style={{ fontSize: '11px', color: '#666' }}>
                      (Use for corrections or special payments)
                    </div>
                  </td>
                  <td className="amount">
                    {canEdit ? (
                      <input
                        type="number"
                        step="0.01"
                        value={formData.manualAdjustment}
                        onChange={(e) => handleFieldChange('manualAdjustment', parseFloat(e.target.value) || 0)}
                        style={{ width: '100%', textAlign: 'right' }}
                      />
                    ) : (
                      `₱${formData.manualAdjustment.toLocaleString()}`
                    )}
                  </td>
                </tr>
                <tr style={{ backgroundColor: '#e3f2fd', fontWeight: 'bold' }}>
                  <td>GROSS PAY (Auto-calculated)</td>
                  <td className="amount">₱{formData.grossPay.toLocaleString()}</td>
                </tr>
              </tbody>
            </table>

            <h4 className="section-heading">Philippine Government Contributions & Deductions</h4>
            <table className="item-table">
              <tbody>
                <tr>
                  <td style={{ width: '60%' }}>
                    SSS Contribution (Employee Share)
                    <div style={{ fontSize: '11px', color: '#666' }}>
                      (Auto-calculated based on Gross Pay)
                    </div>
                  </td>
                  <td className="amount" style={{ backgroundColor: '#f5f5f5' }}>
                    ₱{formData.sssContribution.toLocaleString()}
                  </td>
                </tr>
                <tr>
                  <td>
                    PhilHealth Contribution (Employee Share)
                    <div style={{ fontSize: '11px', color: '#666' }}>
                      (Auto-calculated: 2% of Gross Pay)
                    </div>
                  </td>
                  <td className="amount" style={{ backgroundColor: '#f5f5f5' }}>
                    ₱{formData.philHealthContribution.toLocaleString()}
                  </td>
                </tr>
                <tr>
                  <td>
                    Pag-IBIG Contribution (Employee Share)
                    <div style={{ fontSize: '11px', color: '#666' }}>
                      (Auto-calculated: 2% of Gross Pay, max ₱200)
                    </div>
                  </td>
                  <td className="amount" style={{ backgroundColor: '#f5f5f5' }}>
                    ₱{formData.pagibigContribution.toLocaleString()}
                  </td>
                </tr>
                <tr>
                  <td>
                    Withholding Tax on Compensation (BIR)
                    <div style={{ fontSize: '11px', color: '#666' }}>
                      (Auto-calculated based on BIR Tax Table)
                    </div>
                  </td>
                  <td className="amount" style={{ backgroundColor: '#f5f5f5' }}>
                    ₱{formData.withholdingTax.toLocaleString()}
                  </td>
                </tr>
                <tr>
                  <td>
                    Other Deductions (Loans, Cash Advance, etc.)
                    {canEdit && <span style={{ color: '#28a745', fontSize: '11px' }}> (Editable)</span>}
                  </td>
                  <td className="amount">
                    {canEdit ? (
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        value={formData.otherDeductions}
                        onChange={(e) => handleFieldChange('otherDeductions', parseFloat(e.target.value) || 0)}
                        style={{ width: '100%', textAlign: 'right' }}
                      />
                    ) : (
                      `₱${formData.otherDeductions.toLocaleString()}`
                    )}
                  </td>
                </tr>
                <tr style={{ backgroundColor: '#ffebee', fontWeight: 'bold' }}>
                  <td>TOTAL DEDUCTIONS (Auto-calculated)</td>
                  <td className="amount">₱{formData.totalDeductions.toLocaleString()}</td>
                </tr>
              </tbody>
            </table>

            <table className="item-table" style={{ marginTop: '20px' }}>
              <tbody>
                <tr style={{ backgroundColor: '#c8e6c9', fontWeight: 'bold', fontSize: '16px' }}>
                  <td style={{ width: '60%' }}>NET PAY (Take Home) - Auto-calculated</td>
                  <td className="amount">₱{formData.netPay.toLocaleString()}</td>
                </tr>
              </tbody>
            </table>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose}>
              {editMode ? 'Cancel' : 'Close'}
            </button>
            {!viewMode && (
              <>
                {editMode && !isLocked && (
                  <button type="submit" className="btn btn-primary">
                    Save Changes
                  </button>
                )}
              </>
            )}
          </div>
        </form>
      </div>
    </div>
  );
};

const PayrollProcessing: React.FC = () => {
  const [payrollPeriod, setPayrollPeriod] = useState('2025-03-01 to 2025-03-15');
  const [payrollDetails, setPayrollDetails] = useState<PayrollDetail[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState<PayrollDetail | null>(null);
  const [viewMode, setViewMode] = useState(false);
  const [calculationStatus, setCalculationStatus] = useState<'DRAFT' | 'CALCULATED' | 'CONFIRMED'>('DRAFT');

  useEffect(() => {
    // TODO: Fetch payroll data from API
    // For now, using sample data with Philippine government contributions
    setPayrollDetails([
      {
        employeeCode: 'EMP001',
        employeeName: 'Juan Dela Cruz',
        position: 'Production Supervisor',
        basicSalary: 25000,
        overtimePay: 2500,
        allowances: 3000,
        bonuses: 0,
        manualAdjustment: 0,
        grossPay: 30500,
        sssContribution: 1350,
        philHealthContribution: 850,
        pagibigContribution: 200,
        withholdingTax: 2100,
        otherDeductions: 500,
        totalDeductions: 5000,
        netPay: 25500
      },
      {
        employeeCode: 'EMP002',
        employeeName: 'Maria Santos',
        position: 'Senior Accountant',
        basicSalary: 35000,
        overtimePay: 3850,
        allowances: 5000,
        bonuses: 5000,
        manualAdjustment: 0,
        grossPay: 48850,
        sssContribution: 1800,
        philHealthContribution: 1225,
        pagibigContribution: 200,
        withholdingTax: 5850,
        otherDeductions: 1000,
        totalDeductions: 10075,
        netPay: 38775
      },
      {
        employeeCode: 'EMP003',
        employeeName: 'Pedro Reyes',
        position: 'Production Worker',
        basicSalary: 18000,
        overtimePay: 4050,
        allowances: 2000,
        bonuses: 0,
        manualAdjustment: 0,
        grossPay: 24050,
        sssContribution: 1125,
        philHealthContribution: 675,
        pagibigContribution: 200,
        withholdingTax: 1350,
        otherDeductions: 300,
        totalDeductions: 3650,
        netPay: 20400
      },
      {
        employeeCode: 'EMP004',
        employeeName: 'Ana Garcia',
        position: 'Quality Control Inspector',
        basicSalary: 22000,
        overtimePay: 0,
        allowances: 2500,
        bonuses: 0,
        manualAdjustment: 0,
        grossPay: 24500,
        sssContribution: 1125,
        philHealthContribution: 688,
        pagibigContribution: 200,
        withholdingTax: 1425,
        otherDeductions: 250,
        totalDeductions: 3688,
        netPay: 20812
      },
      {
        employeeCode: 'EMP005',
        employeeName: 'Roberto Tan',
        position: 'Warehouse Manager',
        basicSalary: 30000,
        overtimePay: 1500,
        allowances: 4000,
        bonuses: 2000,
        manualAdjustment: 0,
        grossPay: 37500,
        sssContribution: 1575,
        philHealthContribution: 1013,
        pagibigContribution: 200,
        withholdingTax: 3450,
        otherDeductions: 750,
        totalDeductions: 6988,
        netPay: 30512
      }
    ]);
    setCalculationStatus('CALCULATED');
  }, [payrollPeriod]);

  const filteredDetails = payrollDetails.filter(detail =>
    detail.employeeCode.toLowerCase().includes(searchTerm.toLowerCase()) ||
    detail.employeeName.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const totals = {
    grossPay: filteredDetails.reduce((sum, d) => sum + d.grossPay, 0),
    sss: filteredDetails.reduce((sum, d) => sum + d.sssContribution, 0),
    philHealth: filteredDetails.reduce((sum, d) => sum + d.philHealthContribution, 0),
    pagibig: filteredDetails.reduce((sum, d) => sum + d.pagibigContribution, 0),
    withholdingTax: filteredDetails.reduce((sum, d) => sum + d.withholdingTax, 0),
    totalDeductions: filteredDetails.reduce((sum, d) => sum + d.totalDeductions, 0),
    netPay: filteredDetails.reduce((sum, d) => sum + d.netPay, 0)
  };

  const handleCalculate = () => {
    alert('Recalculating payroll based on latest attendance and rates...');
    setCalculationStatus('CALCULATED');
    // TODO: Trigger payroll calculation API
  };

  const handleConfirm = () => {
    if (window.confirm('Confirm this payroll? This will lock the payroll for payment processing.')) {
      setCalculationStatus('CONFIRMED');
      alert('Payroll confirmed successfully');
      // TODO: Update payroll status in database
    }
  };

  const handleViewDetails = (detail: PayrollDetail) => {
    setSelectedEmployee(detail);
    setViewMode(true);
    setShowModal(true);
  };

  const handleEditDetails = (detail: PayrollDetail) => {
    setSelectedEmployee(detail);
    setViewMode(false);
    setShowModal(true);
  };

  const handleSavePayroll = (updatedDetail: PayrollDetail) => {
    setPayrollDetails(payrollDetails.map(d =>
      d.employeeCode === updatedDetail.employeeCode ? updatedDetail : d
    ));
    setShowModal(false);
  };

  return (
    <MainLayout>
      <div className="master-data-page">
        <div className="page-header">
          <div>
            <h2>Payroll Processing</h2>
            <p className="subtitle">Calculate employee salaries with Philippine government contributions</p>
          </div>
          <div style={{ display: 'flex', gap: '10px' }}>
            {calculationStatus === 'DRAFT' && (
              <button className="btn btn-primary" onClick={handleCalculate}>
                <MdCalculate /> Calculate Payroll
              </button>
            )}
            {calculationStatus === 'CALCULATED' && (
              <button className="btn btn-primary" onClick={handleConfirm}>
                <MdCheck /> Confirm Payroll
              </button>
            )}
            {calculationStatus === 'CONFIRMED' && (
              <span className="badge badge-success" style={{ padding: '10px 20px', fontSize: '14px' }}>
                ✓ CONFIRMED
              </span>
            )}
          </div>
        </div>

        <div className="summary-cards">
          <div className="summary-card">
            <div className="summary-icon">💰</div>
            <div className="summary-content">
              <p className="summary-label">Total Gross Pay</p>
              <p className="summary-value">₱{totals.grossPay.toLocaleString()}</p>
            </div>
          </div>
          <div className="summary-card">
            <div className="summary-icon">📉</div>
            <div className="summary-content">
              <p className="summary-label">Total Deductions</p>
              <p className="summary-value" style={{ color: '#dc3545' }}>₱{totals.totalDeductions.toLocaleString()}</p>
            </div>
          </div>
          <div className="summary-card">
            <div className="summary-icon">💵</div>
            <div className="summary-content">
              <p className="summary-label">Total Net Pay</p>
              <p className="summary-value" style={{ color: '#28a745' }}>₱{totals.netPay.toLocaleString()}</p>
            </div>
          </div>
        </div>

        <div className="page-content">
          <div className="table-controls">
            <div className="search-box">
              <MdSearch className="search-icon" />
              <input
                type="text"
                placeholder="Search employees..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <div className="form-group" style={{ marginLeft: 'auto', minWidth: '250px' }}>
              <label>Payroll Period</label>
              <input
                type="text"
                value={payrollPeriod}
                onChange={(e) => setPayrollPeriod(e.target.value)}
                disabled={calculationStatus === 'CONFIRMED'}
              />
            </div>
          </div>

          <div className="data-table-container">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Employee</th>
                  <th>Position</th>
                  <th>Gross Pay</th>
                  <th>SSS</th>
                  <th>PhilHealth</th>
                  <th>Pag-IBIG</th>
                  <th>W/Tax</th>
                  <th>Total Deductions</th>
                  <th>Net Pay</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {filteredDetails.map((detail, index) => (
                  <tr key={index}>
                    <td>
                      <div className="code">{detail.employeeCode}</div>
                      <div className="name">{detail.employeeName}</div>
                    </td>
                    <td>{detail.position}</td>
                    <td className="amount">₱{detail.grossPay.toLocaleString()}</td>
                    <td className="amount">₱{detail.sssContribution.toLocaleString()}</td>
                    <td className="amount">₱{detail.philHealthContribution.toLocaleString()}</td>
                    <td className="amount">₱{detail.pagibigContribution.toLocaleString()}</td>
                    <td className="amount">₱{detail.withholdingTax.toLocaleString()}</td>
                    <td className="amount" style={{ color: '#dc3545', fontWeight: 'bold' }}>
                      ₱{detail.totalDeductions.toLocaleString()}
                    </td>
                    <td className="amount" style={{ color: '#28a745', fontWeight: 'bold' }}>
                      ₱{detail.netPay.toLocaleString()}
                    </td>
                    <td>
                      <div className="action-buttons">
                        <button
                          className="btn-icon btn-view"
                          onClick={() => handleViewDetails(detail)}
                          title="View Details"
                        >
                          <MdVisibility />
                        </button>
                        {calculationStatus !== 'CONFIRMED' && (
                          <button
                            className="btn-icon btn-edit"
                            onClick={() => handleEditDetails(detail)}
                            title="Edit Payroll"
                          >
                            <MdEdit />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr style={{ backgroundColor: '#f8f9fa', fontWeight: 'bold' }}>
                  <td colSpan={2} style={{ textAlign: 'right' }}>TOTAL:</td>
                  <td className="amount">₱{totals.grossPay.toLocaleString()}</td>
                  <td className="amount">₱{totals.sss.toLocaleString()}</td>
                  <td className="amount">₱{totals.philHealth.toLocaleString()}</td>
                  <td className="amount">₱{totals.pagibig.toLocaleString()}</td>
                  <td className="amount">₱{totals.withholdingTax.toLocaleString()}</td>
                  <td className="amount" style={{ color: '#dc3545' }}>₱{totals.totalDeductions.toLocaleString()}</td>
                  <td className="amount" style={{ color: '#28a745' }}>₱{totals.netPay.toLocaleString()}</td>
                  <td></td>
                </tr>
              </tfoot>
            </table>

            {filteredDetails.length === 0 && (
              <div className="no-data">
                <p>No payroll data found</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {showModal && selectedEmployee && (
        <PayrollModal
          detail={selectedEmployee}
          viewMode={viewMode}
          calculationStatus={calculationStatus}
          onClose={() => setShowModal(false)}
          onSave={handleSavePayroll}
        />
      )}
    </MainLayout>
  );
};

export default PayrollProcessing;
